import { useDeleteApi } from '@/apiError/apiDelete';
import { useGetApiList } from '@/apiError/apiShow';
import '@/assets/style/companyList.scss';
import GroupForm from '@/components/forms/master/account/group/GroupForm';
import CreateModalButton from '@/utils/components/CreateModelButton';
import DeleteModalButton from '@/utils/components/DeleteModalButton';
import SearchBox from '@/utils/components/SearchBox';
import GlobalFormModal from '@/utils/modal/GlobalFormModal';
import GlobalModal from '@/utils/modal/Modal';
import {
  fetchPermissions,
  hasPermission,
} from '@/utils/permissions/userPermission';
import { globalSearch } from '@/utils/search/Search';
import { showToast } from '@/utils/toastMessage/ToastMessage';

import { EditFilled, MailFilled } from '@ant-design/icons';
import { Button, Card, Col, ConfigProvider, Form, Row, Table } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

/**
 * Group Component
 * Displays and manages account groups with search, delete, and edit functionalities.
 *
 * @component
 * @returns {React.ReactElement} Group management interface.
 */
const Group = () => {
  const [form] = Form.useForm();
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [isModalVisible, setIsModalVisible] = useState(false);
  // const [data, setData] = useState();
  // const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTrem] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [selectedgroup, setSelectedgroup] = useState(null);
  const [isMessageModalVisible, setIsMessageModalVisible] = useState(false);
  const [modalType, setModalType] = useState('');
  const [deleteReferenceId, setDeleteReferenceId] = useState(null);
  const [refetchFlag, setRefetchFlag] = useState(false);

  // tanstack-react-query
  const {
    data: { data } = {},
    isLoading: loading,
    isError,
    refetch,
  } = useGetApiList('/account/group/tree-view/');

  const { mutate } = useDeleteApi();

  const navigate = useNavigate();
  /**
   * Hides the modal
   */
  const closeModal = () => {
    setIsModalVisible(false);
    form.resetFields();
    setSelectedgroup(null);
  };

  const openModal = (mode, group = null) => {
    setModalMode(mode);
    if (mode === 'edit' && group) {
      setSelectedgroup(group);
    } else {
      setSelectedgroup(null);
    }
    setIsModalVisible(true);
  };
  const handleCancel = () => {
    setIsMessageModalVisible(false);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <p
          className="company-title"
          style={{ fontWeight: record.children ? '' : 'normal' }} // Bold if parent, normal if child
        >
          {text}
        </p>
      ),
      width: 80,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      width: '10px',
      fixed: 'right',
    },
  ];

  /**
   *Handle search input change.
   *
   * @param {string} value - The search term.
   */
  const handleSearch = (value) => {
    setSearchTrem(value);
  };

  /**
   * Fetches the list of account groups from the API.
   */
  // const fetchGroupList = useEffect( () => {
  // try {
  //   setLoading(true);
  //   const response = await ShowAllData.showDetails(
  //     '/account/group/tree-view/',
  //     navigate,
  //   );
  //   setData(response.data);
  // } finally {
  //   setLoading(false);
  // }
  // }, []);

  useEffect(() => {
    // fetchGroupList();
    setPermissions(fetchPermissions());
  }, []);

  /**
   * Handles the delete button clicked.
   *
   * @param {string} groupId - The id of the group to edit.
   * @param {boolean} isDefault -Whether the group is default group.
   */
  const handleEditClick = (groupId, isDefault) => {
    if (isDefault === false) {
      openModal('edit', groupId);
    } else {
      showToast("Pre-defined account group can't be edited", 'warn');
    }
  };

  const showCancelModal = (referenceId, isDefault) => {
    if (isDefault === true) {
      showToast("Pre-defined group can't be deleted", 'warn');
      return;
    }
    setModalType('delete');
    setIsMessageModalVisible(true);
    setDeleteReferenceId(referenceId);
  };

  /**
   * Handles the delete button click event.
   *
   * @param {string} groupId - The ID of the group to delete.
   * @param {boolean} isDefault - Whether the group is a default group.
   */
  const deleteAccountGroups = async () => {
    if (!deleteReferenceId) return;
    const url = `/account/group/${deleteReferenceId}/`;
    mutate(url, {
      onSuccess: () => {
        setIsMessageModalVisible(false);
        showToast('Group deleted successfully', 'success');
        refetch();
        setRefetchFlag((prev) => !prev);
      },
      onError: () => {
        showToast('Failed to delete the group', 'error');
      },
    });
    // // await DeleteAllData.deleteData(`/account/group/${deleteReferenceId}/`);
    // setIsMessageModalVisible(false);
    // // fetchGroupList();
  };

  /**
   * Recursively generates a tree structure from the account groups data.
   *
   * @param {Array} groups - The account groups data.
   * @returns {Array} The tree structure data.
   */
  const renderTreeData = (data) => {
    const generateTree = (groups) => {
      const treeData = [];

      groups.forEach((group) => {
        const groupItem = {
          key: group.groupId || group.subGroupId,
          name: group.groupName || group.subGroup,

          action: (
            <div className="ant-action">
              <div
                className="ant-button-edit"
                onClick={() =>
                  handleEditClick(
                    group.groupId || group.subGroupId,
                    group.isDefault,
                  )
                }
              >
                <Link size="medium" className="edit">
                  <EditFilled />
                </Link>
              </div>
              {/* <div
                className="ant-button-delete"
                onClick={() =>
                  deleteAccountGroups(
                    group.groupId || group.subGroupId,
                    group.isDefault,
                  )
                }
              >
                <Link size="medium" className="delete">
                  <DeleteFilled />
                </Link>
              </div> */}
              <DeleteModalButton
                showCancelModal={() =>
                  showCancelModal(
                    group.groupId || group.subGroupId,
                    group.isDefault,
                  )
                }
                data={group?.groupId || group.subGroupId}
              />
            </div>
          ),
        };

        if (group.subGroups && group.subGroups.length > 0) {
          // Recursively process subgroups if they exist
          groupItem.children = generateTree(group.subGroups);
        }
        treeData.push(groupItem);
      });

      return treeData;
    };
    return generateTree(filteredTableData || []);
  };
  const filteredTableData = globalSearch(data, searchTerm);

  return (
    <>
      <div
        style={{ position: 'relative', top: 0, marginTop: 0, paddingTop: 0 }}
      >
        <div className="table">
          <Row gutter={[24, 0]}>
            <Col xs={24} xl={24}>
              <Card
                variant="borderless"
                className="circlebox tablespace mb-24"
                title={
                  <>
                    {hasPermission(
                      permissions,
                      'Account Group',
                      'isCreate',
                    ) && (
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <CreateModalButton
                          title="Create New Group"
                          openModal={openModal}
                        />
                      </div>
                    )}
                  </>
                }
                extra={
                  <SearchBox
                    searchPlaceholder="Search group..."
                    handleSearch={handleSearch}
                  />
                }
              >
                <div className="table-responsive">
                  <ConfigProvider
                    theme={{
                      components: {
                        Button: {},
                        Table: {
                          rowHoverBg: '#60c2f0',
                          borderColor: '#12a36cc2',
                        },
                      },
                    }}
                  >
                    <Table
                      size="small"
                      bordered
                      pagination={false}
                      columns={columns}
                      style={{ textAlign: 'center', padding: '25px' }}
                      loading={loading}
                      dataSource={renderTreeData(filteredTableData || [])}
                      scroll={{ x: '100%', y: 400 }}
                      expandable={{ defaultExpandAllRows: true }}
                      footer={() => (
                        <div
                          style={{
                            float: 'left',
                            marginTop: '40px',
                            background: 'none',
                          }}
                        >
                          <Button
                            type="link"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                            }}
                          >
                            {' '}
                            <MailFilled />
                            Contact for Support
                          </Button>
                        </div>
                      )}
                    />
                  </ConfigProvider>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
        <GlobalModal
          isVisible={isMessageModalVisible}
          type={modalType}
          onConfirm={deleteAccountGroups}
          onClose={handleCancel}
        />
        <GlobalFormModal
          isVisible={isModalVisible}
          title={modalMode === 'create' ? 'Create Group' : 'Update Group'}
          onClose={closeModal}
          loading={false}
        >
          <GroupForm
            mode={modalMode}
            referenceId={selectedgroup}
            onClose={closeModal}
            refreshTable={refetch}
            refetchFlag={refetchFlag}
            setRefetchFlag={setRefetchFlag}
          />
        </GlobalFormModal>
      </div>
    </>
  );
};

export default Group;
